/* ==========================================================================
   SECTION-AWARE GENERATION
   --------------------------------------------------------------------------
   The bug this fixes: the previous filler generator produced a mixed bag of
   content and `assemblePaper` then re-stamped each item's `section` field to
   whichever sections the student had selected. Choose "Science only" on the
   TEAS and you were served vocabulary and grammar questions wearing a
   "Science" label.

   Here, content is chosen BY section. Each exam section is classified into a
   content kind, and only generators matching that kind are used. Items are
   tagged with the section they were actually built for, so filtering,
   the results breakdown and mastery tracking all agree.

   Loads after exam-generator-topics.js, before exam-engine.js.
   ========================================================================== */
(function () {
  'use strict';

  function mulberry32(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      var t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  function pick(r, a) { return a[Math.floor(r() * a.length) % a.length]; }
  function shuffleOpts(r, correct, wrongs) {
    var o = [correct].concat(wrongs.slice(0, 3));
    for (var i = o.length - 1; i > 0; i--) {
      var j = Math.floor(r() * (i + 1));
      var t = o[i]; o[i] = o[j]; o[j] = t;
    }
    return { opts: o, a: o.indexOf(correct) };
  }
  function yt(q, l) {
    return { t: l || ('YouTube — ' + q), u: 'https://www.youtube.com/results?search_query=' + encodeURIComponent(q), k: 'video' };
  }
  var REF = {
    owl: { t: 'Purdue OWL — Grammar & Mechanics', u: 'https://owl.purdue.edu/owl/general_writing/grammar/index.html', k: 'reference' },
    mw: { t: 'Merriam-Webster Dictionary', u: 'https://www.merriam-webster.com/', k: 'reference' },
    bio: { t: 'OpenStax — Biology 2e', u: 'https://openstax.org/details/books/biology-2e', k: 'scholarly' },
    ap: { t: 'OpenStax — Anatomy & Physiology 2e', u: 'https://openstax.org/details/books/anatomy-and-physiology-2e', k: 'scholarly' },
    chem: { t: 'OpenStax — Chemistry 2e', u: 'https://openstax.org/details/books/chemistry-2e', k: 'scholarly' },
    phys: { t: 'OpenStax — College Physics 2e', u: 'https://openstax.org/details/books/college-physics-2e', k: 'scholarly' },
    stat: { t: 'OpenStax — Introductory Statistics', u: 'https://openstax.org/details/books/introductory-statistics', k: 'scholarly' },
    sp: { t: 'StatPearls (NCBI Bookshelf)', u: 'https://www.ncbi.nlm.nih.gov/books/NBK430685/', k: 'scholarly' }
  };

  /* =====================================================================
     1. CLASSIFY A SECTION
     ===================================================================== */
  function kindFor(exam, sec) {
    var id = String(sec.id || '').toLowerCase();
    var nm = String(sec.name || '').toLowerCase();
    var s = id + ' ' + nm;
    var clinicalExam = !!(exam && exam.science) && exam.group !== 'course';

    /* Explicit, most specific first. */
    if (/vocab|word knowledge|^wk$|\bwk\b/.test(s)) return 'vocab';
    if (/read|comprehen|passage|cars|\brc\b|paragraph/.test(s)) return 'reading';
    if (/grammar|english|language usage|elar|composition|writing|\bpc\b|structure|mechanic/.test(s)) return 'grammar';
    if (/math|quant|arith|algebra|calculus|precalc|numer|measurement|data|\bar\b|\bmk\b|statistic|probab/.test(s)) return 'math';

    if (clinicalExam) {
      if (/pharm|drug|medicat/.test(s)) return 'clinical';
      if (/lab|value|diagnostic/.test(s)) return 'clinical';
      if (/infect|safety|precaution|hygiene/.test(s)) return 'clinical';
      if (/anatom|physiolog|cell|body|system|cardio|resp|renal|neuro|musculo|gi\b|endocrine/.test(s)) return 'science';
      if (/science|biolog|chem|physic/.test(s)) return 'science';
      return 'clinical';
    }

    if (/science|biolog|chem|physic|anatom|physiolog|natural|\bgs\b|life science|earth/.test(s)) return 'science';
    if (/social|history|govern|human|econom|psych|geograph/.test(s)) return 'concept';
    if (/logic|spatial|reason|analy|verbal/.test(s)) return 'verbalreason';
    return 'concept';
  }

  /* =====================================================================
     2. CONTENT BY KIND
     ===================================================================== */

  /* ---------- READING ---------- */
  var PASSAGES = [
    {
      id: 'p-libraries',
      text: '<p>When the city council proposed closing three branch libraries, it argued that circulation figures had fallen by a third over a decade. Library staff did not dispute the figures. They pointed out instead that the branches now function largely as warm rooms, job-application desks and after-school shelters, none of which appears in a circulation count. The council\'s own survey found that 61 percent of weekday visitors borrowed nothing at all.</p>',
      qs: [
        { q: 'The main idea of the passage is that:',
          a: 'The measure used to judge the branches does not capture what they are actually used for',
          w: ['Circulation figures at the branches have risen over the last decade',
              'The city council has already reversed its decision to close the branches',
              'Library staff disagree with the accuracy of the council\'s statistics'],
          why: 'The passage turns on a mismatch between the <em>metric</em> (circulation) and the <em>use</em> (shelter, job applications, after-school care). Staff explicitly "did not dispute the figures", so the last option contradicts the text.' },
        { q: 'The statistic that 61 percent of weekday visitors borrowed nothing is used to:',
          a: 'Support the staff\'s point that circulation understates library use',
          w: ['Show that the branches are rarely visited on weekdays',
              'Prove that the council\'s survey was poorly designed',
              'Demonstrate that borrowing has increased among weekend visitors'],
          why: 'It is the council\'s own figure, offered in support of the staff\'s argument. Note it says nothing about how <em>many</em> people visit — only what proportion of visitors borrow.' },
        { q: 'Which statement from the passage is a fact rather than an opinion?',
          a: 'Circulation figures fell by about a third over a decade',
          w: ['The branches function largely as warm rooms',
              'The branches should remain open',
              'The council\'s reasoning is flawed'],
          why: 'A fact can be verified against a record. "Function largely as" is an interpretation, and the other two are judgements the passage never states outright.' }
      ]
    },
    {
      id: 'p-sleep',
      text: '<p>Adolescents\' internal clocks shift later at puberty, a change observed across cultures and not explained by screen use alone. Schools that moved start times from 7:30 to 8:30 reported students sleeping roughly 30 minutes longer on average, with modest gains in attendance. Critics note that later starts push sports and jobs into the evening, and that districts adopting the change were also those able to afford new bus schedules.</p>',
      qs: [
        { q: 'The author\'s attitude toward later school start times is best described as:',
          a: 'Balanced — presenting evidence of benefit alongside practical objections',
          w: ['Strongly supportive, dismissing objections as unfounded',
              'Openly hostile to the idea of changing start times',
              'Uninterested in whether the change has any effect'],
          why: 'The passage reports gains and then gives critics space, without endorsing either. Look at what the final sentence does: it raises a confounder rather than settling the question.' },
        { q: 'The critics\' point about districts that could "afford new bus schedules" suggests:',
          a: 'The districts adopting the change may have differed in other ways that affected outcomes',
          w: ['Bus schedules are the main cause of adolescent sleep loss',
              'Later start times are too expensive for any district to adopt',
              'Attendance gains were larger in wealthier districts'],
          why: 'This is a confounding-variable objection: wealthier districts may improve attendance for reasons unrelated to sleep. The passage never claims the last option — that would need a comparison it does not make.' },
        { q: 'According to the passage, the shift in adolescent body clocks is:',
          a: 'Observed across cultures and not fully explained by screen use',
          w: ['Caused primarily by evening screen exposure',
              'Limited to students in districts with early start times',
              'A recent development first noticed in the last decade'],
          why: 'The first sentence states both points directly. "Not explained by screen use alone" is a deliberate qualification the distractor removes.' }
      ]
    },
    {
      id: 'p-antibiotic',
      text: '<p>A clinic reduced antibiotic prescriptions for sore throat by 40 percent after introducing a rapid strep test at the front desk. Prescriptions did not fall in a comparable clinic without the test. Researchers caution that the intervention coincided with a national awareness campaign, and that the participating clinic had volunteered.</p>',
      qs: [
        { q: 'The comparison clinic is included in order to:',
          a: 'Provide a baseline against which the change can be judged',
          w: ['Show that rapid strep tests are unnecessary',
              'Demonstrate that the awareness campaign had no effect',
              'Prove that volunteering clinics are more effective'],
          why: 'A comparison group exists to answer "compared with what?". Without it, any change might reflect a general trend rather than the intervention.' },
        { q: 'Which would MOST strengthen the claim that the rapid test caused the reduction?',
          a: 'Clinics were randomly assigned to receive the test or not',
          w: ['The clinic reported high satisfaction with the new test',
              'The national campaign ran in both clinics\' regions',
              'Prescriptions had been falling slowly before the test arrived'],
          why: 'Random assignment removes the volunteering problem the passage raises — it is the single strongest causal design. The last option would <em>weaken</em> the claim by suggesting a pre-existing trend.' }
      ]
    },
    {
      id: 'p-coral',
      text: '<p>Coral reefs cover less than one percent of the ocean floor yet shelter around a quarter of marine species. When water warms even briefly, corals expel the algae living in their tissue and lose their colour — a process called bleaching. Bleached coral is not dead, and reefs can recover if conditions return to normal quickly. Repeated bleaching within a few years, however, leaves little time for recovery.</p>',
      qs: [
        { q: 'According to the passage, bleached coral is:',
          a: 'Alive, and capable of recovery if conditions improve quickly',
          w: ['Dead, and unable to recover under any conditions',
              'Permanently discoloured but otherwise unaffected',
              'More resistant to future temperature rises'],
          why: 'The passage states this directly: "Bleached coral is not dead". The distinction between bleached and dead is precisely the point being made.' },
        { q: 'The word "expel" as used in the passage most nearly means:',
          a: 'Drive out',
          w: ['Absorb', 'Nourish', 'Conceal'],
          why: 'Corals drive out the algae living in their tissue, which is why they lose colour. Substituting each option back into the sentence confirms only one preserves the meaning.' }
      ]
    }
  ];

  function genReading(r, sectionId, difficulty, n) {
    var out = [];
    var order = PASSAGES.slice();
    for (var i = order.length - 1; i > 0; i--) { var j = Math.floor(r() * (i + 1)); var t = order[i]; order[i] = order[j]; order[j] = t; }
    var pi = 0, qi = 0, guard = 0;
    while (out.length < n && guard++ < n * 6) {
      var P = order[pi % order.length];
      var Q = P.qs[qi % P.qs.length];
      var s = shuffleOpts(r, Q.a, Q.w);
      out.push({
        section: sectionId, difficulty: difficulty, type: 'mcq',
        stimulus: P.text, q: Q.q, opts: s.opts, a: s.a,
        why: '<p>' + Q.why + '</p><p>Reading items are answered from the passage, not from prior knowledge. If you cannot point to the line that supports your choice, it is the wrong choice.</p>',
        refs: [REF.owl, yt('reading comprehension main idea inference strategy', 'Video — Reading comprehension strategy')]
      });
      qi++;
      if (qi % P.qs.length === 0) pi++;
    }
    return out;
  }

  /* ---------- GRAMMAR / ENGLISH ---------- */
  var GRAM = [
    { c: 'The committee has published its report, and it\'s available online.',
      w: ['The committee has published it\'s report, and its available online.',
          'The committee has published its report, and its available online.',
          'The committee has published it\'s report, and it\'s available online.'],
      t: 'its vs it\'s', why: '<strong>It\'s</strong> is only ever "it is" or "it has". <strong>Its</strong> is possessive and takes no apostrophe, like <em>his</em> and <em>hers</em>.' },
    { c: 'The box of surgical instruments was left on the counter.',
      w: ['The box of surgical instruments were left on the counter.',
          'The boxes of surgical instrument was left on the counter.',
          'The box of surgical instruments have been left on the counter.'],
      t: 'subject-verb agreement', why: 'The subject is <strong>box</strong>, singular. "Of surgical instruments" is a prepositional phrase — cross it out and the agreement is obvious.' },
    { c: 'Having finished the assignment, she submitted it before midnight.',
      w: ['Having finished the assignment, it was submitted before midnight.',
          'Having finished the assignment, midnight arrived before submission.',
          'Having finished the assignment, the submission was before midnight.'],
      t: 'dangling modifiers', why: 'The noun after an introductory participial phrase must be the one performing the action. Only <em>she</em> can have finished the assignment.' },
    { c: 'Fewer applicants enrolled this term, so there was less demand for housing.',
      w: ['Less applicants enrolled this term, so there was fewer demand for housing.',
          'Fewer applicants enrolled this term, so there was fewer demand for housing.',
          'Less applicants enrolled this term, so there was less demand for housing.'],
      t: 'fewer vs less', why: '<strong>Fewer</strong> for things you can count, <strong>less</strong> for quantities you cannot.' },
    { c: 'To whom should the completed form be returned?',
      w: ['To who should the completed form be returned?',
          'To whom should the completed form be returned to?',
          'Whom should the completed form be returned?'],
      t: 'who vs whom', why: 'After a preposition, use <strong>whom</strong>. Test it by answering with him/her (whom) or he/she (who).' },
    { c: 'The nurse checked the chart, verified the order and administered the dose.',
      w: ['The nurse checked the chart, verifying the order and administered the dose.',
          'The nurse checked the chart, verified the order and was administering the dose.',
          'The nurse was checking the chart, verified the order and to administer the dose.'],
      t: 'parallel structure', why: 'Items in a series must share the same grammatical form — here three past-tense verbs.' },
    { c: 'The students\' results were posted on Friday.',
      w: ['The student\'s results were posted on Friday (referring to many students).',
          'The students results were posted on Friday.',
          'The students\'s results were posted on Friday.'],
      t: 'plural possessives', why: 'A plural noun already ending in -s takes the apostrophe <strong>after</strong> the s.' },
    { c: 'The new policy will affect staffing; its effect on morale is unclear.',
      w: ['The new policy will effect staffing; its affect on morale is unclear.',
          'The new policy will affect staffing; its affect on morale is unclear.',
          'The new policy will effect staffing; its effect on morale is unclear.'],
      t: 'affect vs effect', why: '<strong>RAVEN</strong> — Remember, Affect is a Verb, Effect is a Noun. That covers the great majority of uses.' },
    { c: 'Because the shipment was delayed, the clinic rescheduled the clinic day.',
      w: ['Because the shipment was delayed, therefore the clinic rescheduled the clinic day.',
          'Because the shipment was delayed the clinic rescheduled the clinic day',
          'The shipment was delayed, the clinic rescheduled the clinic day.'],
      t: 'subordination and comma splices', why: 'One connector per relationship. "Because … therefore" doubles it, and the last option is a comma splice.' },
    { c: 'Neither the physician nor the nurses were available for comment.',
      w: ['Neither the physician nor the nurses was available for comment.',
          'Neither the physician or the nurses were available for comment.',
          'Neither the physician nor the nurses is available for comment.'],
      t: 'neither/nor agreement', why: 'With <em>neither … nor</em>, the verb agrees with the <strong>nearer</strong> subject — here <em>nurses</em>, so <em>were</em>.' }
  ];

  function genGrammar(r, sectionId, difficulty, n) {
    var out = [], seen = {}, i = 0, guard = 0;
    while (out.length < n && guard++ < n * 6) {
      var G = GRAM[i++ % GRAM.length];
      var s = shuffleOpts(r, G.c, G.w);
      var key = G.c;
      if (seen[key] && out.length < GRAM.length) continue;
      seen[key] = true;
      out.push({
        section: sectionId, difficulty: difficulty, type: 'mcq',
        q: 'Which sentence is written correctly?', opts: s.opts, a: s.a,
        why: '<p>This item tests <strong>' + G.t + '</strong>.</p><p>' + G.why + '</p>' +
          '<p>Read each option in your head before choosing. Errors of this type are far easier to hear than to see.</p>',
        refs: [REF.owl, yt(G.t + ' grammar rule explained', 'Video — ' + G.t)]
      });
    }
    return out;
  }

  /* ---------- VOCABULARY ---------- */
  var VOCAB = [
    ['abate', 'to lessen in intensity', ['to intensify', 'to postpone', 'to confuse'], '"Tax abatement" is a reduction — the everyday phrase points straight at the meaning.'],
    ['candid', 'honest and direct', ['secretive', 'hesitant', 'formal'], 'A "candid photo" is one taken without posing — unguarded, unarranged.'],
    ['prudent', 'showing careful judgement', ['reckless', 'generous', 'talkative'], 'Related to "prudence" — caution exercised in practical matters.'],
    ['obstinate', 'stubbornly refusing to change', ['easily persuaded', 'unusually clever', 'openly hostile'], 'Note the near-antonym distractor; half-remembering the word pulls you to the wrong pole.'],
    ['tenacious', 'holding firmly, persistent', ['easily discouraged', 'physically weak', 'widely admired'], 'A tenacious grip does not let go — the physical sense carries into the figurative one.'],
    ['benign', 'gentle and harmless', ['dangerous', 'enormous', 'temporary'], 'In medicine a benign tumour is non-cancerous — harmless is the shared core.'],
    ['ambiguous', 'open to more than one interpretation', ['perfectly clear', 'deliberately false', 'extremely brief'], 'Ambiguous is not the same as false. It means unclear <em>which</em> meaning applies.'],
    ['exacerbate', 'to make a problem worse', ['to relieve', 'to explain', 'to delay'], 'Clinically, symptoms are "exacerbated" — made worse. The opposite is to alleviate.'],
    ['lucid', 'clear and easy to understand', ['confusing', 'lengthy', 'dishonest'], 'A patient described as lucid is thinking clearly — the same root as "elucidate".'],
    ['frugal', 'careful with money', ['wasteful', 'wealthy', 'dishonest'], 'Frugal is about spending habit, not about wealth — a rich person can be frugal.'],
    ['transient', 'lasting only a short time', ['permanent', 'enormous', 'repeated'], 'A transient ischaemic attack is brief and resolves — the word carries the duration.'],
    ['meticulous', 'extremely careful about detail', ['careless', 'hurried', 'indifferent'], 'Meticulous documentation means nothing was skipped.'],
    ['astute', 'shrewd and perceptive', ['naive', 'generous', 'aggressive'], 'An astute observation catches what others missed.'],
    ['ameliorate', 'to make better', ['to worsen', 'to postpone', 'to confuse'], 'Shares a root with "meliorate" — to improve.'],
    ['laconic', 'using very few words', ['long-winded', 'unfriendly', 'careless'], 'Laconic is about brevity, not rudeness — a common confusion.'],
    ['diligent', 'showing steady, careful effort', ['lazy', 'brilliant', 'anxious'], 'Diligence is about persistence of effort, not level of talent.']
  ];

  function genVocab(r, sectionId, difficulty, n) {
    var out = [], i = 0, guard = 0, seen = {};
    while (out.length < n && guard++ < n * 6) {
      var V = VOCAB[i++ % VOCAB.length];
      if (seen[V[0]] && out.length < VOCAB.length) continue;
      seen[V[0]] = true;
      var s = shuffleOpts(r, V[1], V[2]);
      out.push({
        section: sectionId, difficulty: difficulty, type: 'mcq',
        q: '"' + V[0].toUpperCase() + '" most nearly means:', opts: s.opts, a: s.a,
        why: '<p><strong>' + V[0].charAt(0).toUpperCase() + V[0].slice(1) + '</strong> means <strong>' + V[1] + '</strong>.</p>' +
          '<p>' + V[3] + '</p>' +
          '<p>One option is always a near-antonym. When a word is unfamiliar, decide first whether the sentence needs a positive or negative word — that alone eliminates half the options.</p>',
        refs: [REF.mw]
      });
    }
    return out;
  }

  /* ---------- SCIENCE ---------- */
  var SCI = [
    ['Which organelle carries out most ATP production in a eukaryotic cell?', 'Mitochondrion', ['Ribosome', 'Golgi apparatus', 'Lysosome'],
     'Oxidative phosphorylation happens on the inner mitochondrial membrane. Ribosomes build protein, the Golgi packages it, lysosomes digest waste.', REF.bio],
    ['Which blood vessel carries oxygenated blood from the lungs to the heart?', 'Pulmonary vein', ['Pulmonary artery', 'Aorta', 'Superior vena cava'],
     'This is the exception students trip on: the pulmonary <em>vein</em> carries oxygenated blood, and the pulmonary <em>artery</em> carries deoxygenated blood. Arteries are defined by direction away from the heart, not by oxygen content.', REF.ap],
    ['In DNA, adenine pairs with which base?', 'Thymine', ['Guanine', 'Cytosine', 'Uracil'],
     'A–T and G–C in DNA. Uracil replaces thymine in RNA only, which is the distractor here.', REF.bio],
    ['Which body system is primarily responsible for regulating blood calcium?', 'Endocrine system', ['Lymphatic system', 'Integumentary system', 'Respiratory system'],
     'Parathyroid hormone raises blood calcium and calcitonin lowers it — both endocrine. Bone acts as the reservoir but the control is hormonal.', REF.ap],
    ['A solution with a pH of 4 compared with one of pH 6 is:', '100 times more acidic', ['2 times more acidic', '10 times more acidic', '2 times more basic'],
     'The pH scale is logarithmic — each unit is a tenfold change in hydrogen ion concentration. Two units is 10 × 10 = 100.', REF.chem],
    ['Which type of muscle tissue is involuntary and striated?', 'Cardiac muscle', ['Skeletal muscle', 'Smooth muscle', 'Connective tissue'],
     'Skeletal is striated but voluntary; smooth is involuntary but not striated. Cardiac is the only one that is both.', REF.ap],
    ['During which phase of the cell cycle is DNA replicated?', 'S phase', ['G1 phase', 'G2 phase', 'M phase'],
     'S stands for synthesis. G1 and G2 are growth and checkpoint phases; M is division itself.', REF.bio],
    ['What is the primary function of the loop of Henle in the kidney?', 'Concentrating urine by establishing an osmotic gradient', ['Filtering blood cells out of plasma', 'Producing renin to raise blood pressure', 'Secreting insulin into the filtrate'],
     'Filtration happens at the glomerulus. The loop\'s counter-current mechanism is what makes concentrated urine possible.', REF.ap],
    ['Which of the following is a chemical rather than a physical change?', 'Iron rusting in damp air', ['Ice melting to water', 'Salt dissolving in water', 'Water boiling to steam'],
     'A chemical change produces a new substance with new properties. The other three are reversible changes of state or dispersion.', REF.chem],
    ['Newton\'s third law states that:', 'For every action there is an equal and opposite reaction', ['Force equals mass times acceleration', 'An object at rest stays at rest unless acted on', 'Energy cannot be created or destroyed'],
     'The second option is the second law, the third is the first law, and the fourth is conservation of energy.', REF.phys],
    ['Which macromolecule serves as the body\'s primary structural and enzymatic component?', 'Protein', ['Carbohydrate', 'Lipid', 'Nucleic acid'],
     'Proteins build tissue and catalyse reactions. Carbohydrates are the main quick energy source, lipids store energy and form membranes, nucleic acids carry information.', REF.bio],
    ['Homeostatic control of body temperature is an example of:', 'Negative feedback', ['Positive feedback', 'Feedforward amplification', 'Passive diffusion'],
     'Negative feedback opposes change and restores a set point. Positive feedback amplifies — childbirth and clotting are the rare examples.', REF.ap]
  ];

  function genScience(r, sectionId, difficulty, n) {
    var out = [], i = 0, guard = 0, seen = {};
    while (out.length < n && guard++ < n * 6) {
      var S = SCI[i++ % SCI.length];
      if (seen[S[0]] && out.length < SCI.length) continue;
      seen[S[0]] = true;
      var s = shuffleOpts(r, S[1], S[2]);
      out.push({
        section: sectionId, difficulty: difficulty, type: 'mcq',
        q: S[0], opts: s.opts, a: s.a,
        why: '<p>The answer is <strong>' + S[1] + '</strong>.</p><p>' + S[3] + '</p>',
        refs: [S[4], yt(S[0].slice(0, 60), 'Video — related explanation')]
      });
    }
    return out;
  }

  /* ---------- VERBAL REASONING (logic/spatial/analysis) ---------- */
  var LOGIC = [
    ['If all members of the committee are volunteers, and Sara is not a volunteer, what follows?',
     'Sara is not a member of the committee', ['Sara is a member of the committee', 'Some volunteers are not committee members', 'No conclusion can be drawn'],
     'This is the contrapositive: from "all A are B" and "not B", it follows validly that "not A". It is the only inference the premises license.'],
    ['Complete the series: 4, 9, 19, 39, ___',
     '79', ['59', '69', '78'],
     'Each term doubles and adds 1: 4→9→19→39→79. Test the ratio and the difference before assuming a simple pattern.'],
    ['Book is to library as painting is to:',
     'Gallery', ['Artist', 'Canvas', 'Frame'],
     'The relationship is item to the place that houses collections of it. Artist is the maker, canvas the material, frame the container of a single work.'],
    ['If no reptiles are warm-blooded and all snakes are reptiles, then:',
     'No snakes are warm-blooded', ['Some snakes are warm-blooded', 'All warm-blooded animals are snakes', 'Nothing follows'],
     'A valid syllogism. The conclusion is forced by the premises regardless of whether the premises are actually true.'],
    ['Which does not belong: triangle, square, circle, pentagon?',
     'Circle', ['Triangle', 'Square', 'Pentagon'],
     'The others are polygons with straight sides and vertices; a circle has neither. Look for the property three share and one lacks.']
  ];

  function genLogic(r, sectionId, difficulty, n) {
    var out = [], i = 0, guard = 0;
    while (out.length < n && guard++ < n * 6) {
      var L = LOGIC[i++ % LOGIC.length];
      var s = shuffleOpts(r, L[1], L[2]);
      out.push({
        section: sectionId, difficulty: difficulty, type: 'mcq',
        q: L[0], opts: s.opts, a: s.a,
        why: '<p>The answer is <strong>' + L[1] + '</strong>.</p><p>' + L[3] + '</p>',
        refs: [yt('logical reasoning aptitude test technique', 'Video — Logical reasoning technique')]
      });
    }
    return out;
  }

  /* =====================================================================
     3. PUBLIC API
     ===================================================================== */
  function generateForSection(o) {
    var r = mulberry32((((o.seed || 1) + (o.sectionId || '').length * 977) * 2654435761) % 2147483647);
    var d = (o.difficulty && o.difficulty !== 'combined') ? o.difficulty : null;
    var diffs = ['easy', 'moderate', 'hard', 'advanced'];
    var n = Math.max(0, o.count || 0);
    if (!n) return [];
    var kind = o.kind;
    var out;

    if (kind === 'reading') out = genReading(r, o.sectionId, d || 'moderate', n);
    else if (kind === 'grammar') out = genGrammar(r, o.sectionId, d || 'moderate', n);
    else if (kind === 'vocab') out = genVocab(r, o.sectionId, d || 'moderate', n);
    else if (kind === 'science') out = genScience(r, o.sectionId, d || 'moderate', n);
    else if (kind === 'verbalreason') out = genLogic(r, o.sectionId, d || 'moderate', n);
    else if (kind === 'math') {
      var base = (window.XP_GEN && (window.XP_GEN.base || window.XP_GEN.generate));
      out = base ? base({ count: n, seed: (o.seed || 1) + 31, difficulty: o.difficulty, prefix: o.prefix,
        course: { id: o.examId, name: o.examName || '', topics: [], kind: 'quant' } }) : [];
      out.forEach(function (it) { it.section = o.sectionId; });
    } else {
      /* concept / clinical — reuse the topic generator, then force the label */
      var topics = window.XP_GEN && window.XP_GEN.generate;
      out = topics ? topics({ count: n, seed: (o.seed || 1) + 57, difficulty: o.difficulty,
        course: { id: o.examId, name: o.examName || '', topics: [], kind: kind === 'clinical' ? 'clinical' : 'quant' },
        prefix: o.prefix }) : [];
      out.forEach(function (it) { it.section = o.sectionId; });
    }

    out = out.slice(0, n);
    out.forEach(function (it, i) {
      if (!d) it.difficulty = diffs[i % 4];
      it.exam = o.examId;
      it.generated = true;
      it.id = (o.prefix || 'gen') + '-' + o.sectionId + '-' + (o.seed || 0) + '-' + i;
    });
    return out;
  }

  /* Build a whole paper, respecting the selected sections and their weights. */
  function generatePaper(o) {
    var exam = o.exam;
    var want = Math.max(0, o.count || 0);
    if (!want || !exam) return [];

    var all = exam.sections || [];
    var chosen = all.filter(function (s) {
      return !o.sections || !o.sections.length || o.sections.indexOf(s.id) > -1;
    });
    if (!chosen.length) chosen = all.slice();
    if (!chosen.length) {
      return generateForSection({ kind: 'concept', sectionId: 'general', count: want,
        seed: o.seed, difficulty: o.difficulty, examId: exam.id, examName: exam.name, prefix: exam.id });
    }

    /* Split the requested count across the chosen sections in proportion to
       their blueprint weights, so a filtered paper still mirrors the exam. */
    var totalW = 0;
    chosen.forEach(function (s) { totalW += (s.weight || 10); });
    var alloc = [], used = 0;
    chosen.forEach(function (s, i) {
      var nItems = (i === chosen.length - 1)
        ? want - used
        : Math.round(want * ((s.weight || 10) / totalW));
      if (nItems < 0) nItems = 0;
      used += nItems;
      alloc.push({ sec: s, n: nItems });
    });

    var out = [];
    alloc.forEach(function (a) {
      if (!a.n) return;
      out = out.concat(generateForSection({
        kind: kindFor(exam, a.sec),
        sectionId: a.sec.id,
        count: a.n,
        seed: (o.seed || 1),
        difficulty: o.difficulty,
        examId: exam.id,
        examName: exam.name,
        prefix: exam.id
      }));
    });

    /* Interleave so sections do not arrive in solid blocks. */
    var r = mulberry32(((o.seed || 1) * 7919) % 2147483647);
    for (var i = out.length - 1; i > 0; i--) {
      var j = Math.floor(r() * (i + 1));
      var t = out[i]; out[i] = out[j]; out[j] = t;
    }
    return out.slice(0, want);
  }

  window.XP_SECTIONS = {
    kindFor: kindFor,
    generateForSection: generateForSection,
    generatePaper: generatePaper
  };
})();
